import type React from 'react';
import { useEffect, useState } from 'react';

const COPIED_MESSAGE_TIMEOUT = 1000;

/**
 * Hook to update an isCopied variable with a timeout to reset the value for usage in
 * copy to clipboard UXs.
 * @param {() => void} onCopy - Optional callback to be called when the text is copied.
 * @param {number} timeout - Optional timeout to reset `isCopied`, defaults to 1000ms.
 * @returns {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} - A tuple of the isCopied state and the setIsCopied function.
 *
 * @example
 * const [isCopied, setIsCopied] = useCopyToClipboard();
 *
 * return (
 *   <>
 *     <button onClick={() => setIsCopied(true)}>
 *     {isCopied ? 'Copied!' : 'Copy'}
 *   </>
 * )
 */
export function useCopyToClipboard(
  onCopy?: () => void,
  timeout: number = COPIED_MESSAGE_TIMEOUT,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  //  Handle setting the text of the "copy" button.
  const [isCopied, setIsCopied] = useState(false);

  //  Handle resetting the button after copying,
  useEffect(() => {
    if (isCopied) {
      //  Call the onCopy callback if it exists.
      onCopy?.();

      //  Reset the button after the timeout.
      setTimeout(() => {
        setIsCopied(false);
      }, timeout);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCopied, timeout]);

  return [
    isCopied,
    setIsCopied,
  ];
}
